/**
 * Pricing-migration notice configuration — Yoast/Platform#4576.
 *
 * Legal requires MyYoast to carry a notice for thirty days before the customers we were never able to
 * reach may be moved to the new packaging and pricing. Who sees it is decided server-side
 * (`GET /api/Customers/:id/subscriptions/legacy-pricing-notice`), and so is the new price it states —
 * that figure depends on the customer's product, currency, billing term and site count, so it cannot
 * be a constant. What is left here is the dates the copy names and the window it shows in.
 */

import { createFeatureFlag } from "../functions/featureFlags";

// The switch in front of the notice. It is deliberately separate from the dates below, so that the
// window opening is not by itself what puts the notice in front of a customer — turning it off is a
// one-line revert that needs no date arithmetic.
export const ENABLE_PRICING_MIGRATION_BANNER = true;

/** When the thirty-day window opens. */
export const BANNER_STARTS = new Date( "2026-10-01T00:00:00Z" );

/** When it closes. Thirty days after it opens, per legal's instruction. */
export const BANNER_ENDS = new Date( "2026-11-02T00:00:00Z" );

/** The date the new renewal prices take effect. */
export const PRICE_EFFECTIVE_DATE = new Date( "2026-11-03T00:00:00Z" );

/** The date by which the customer should have confirmed their contact details. */
export const CONTACT_UPDATE_DEADLINE = new Date( "2026-11-02T00:00:00Z" );

/**
 * Where "Contact support" goes.
 *
 * A plain link rather than the Help Scout beacon `SupportCard` opens: the beacon is injected by Google
 * Tag Manager, so it is absent in development and whenever a browser blocks it, and clicking then does
 * nothing at all. A notice legal requires us to show must not offer a dead control.
 */
export const SUPPORT_URL = "https://yoast.com/help/";

/**
 * Whether the pricing-migration notice should be displayed.
 *
 * Enable via:
 * - a development build, which is always on (see the branch below)
 * - `ENABLE_PRICING_MIGRATION_BANNER` above, which is what turns it off again
 * - Environment variable: `REACT_APP_ENABLE_PRICING_MIGRATION_BANNER=true`
 * - Query param: `?test_pricing_migration_banner` (persists for the browser session)
 *
 * Note this only decides whether the notice may appear at all. Whether a given customer is in the
 * cohort is the server's answer, and the banner needs both.
 *
 * @returns {boolean} True if the notice should be displayed.
 */
export const isPricingMigrationBannerActive = createFeatureFlag( "pricing_migration_banner", () => {
	// Always on in development, so logging in as an eligible customer is enough to see the notice —
	// no query param, no container restart to set an environment variable. `process.env.NODE_ENV` is
	// substituted at build time by Vite's `define`, so this is `"production"` for every real build
	// (staging included, since staging builds set NODE_ENV=production) and the branch is dropped from
	// the bundle. It therefore cannot leak the notice to a customer.
	if ( process.env.NODE_ENV === "development" ) {
		return true;
	}

	if ( ! ENABLE_PRICING_MIGRATION_BANNER ) {
		return false;
	}

	const now = new Date();

	return now >= BANNER_STARTS && now <= BANNER_ENDS;
} );
